import React, { useCallback, useEffect, useState, useRef } from "react";
import { isEmpty, forEach } from "ramda";
import { EpubView } from "react-reader";
import ePub from "epubjs";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/styles";
import debounce from "lodash.debounce";
import { HIDE_VIEWER } from "media-viewer/mediaViewerRedux";
import AppBar from "@material-ui/core/AppBar";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CircularProgress from "@material-ui/core/CircularProgress";
import Close from "@material-ui/icons/Close";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import Slider from "@material-ui/core/Slider";
import TocDialog from "media-viewer/TocDialog";
import TocIcon from "@material-ui/icons/Toc";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import SearchDialog from "media-viewer/SearchDialog";

const useStyles = makeStyles(theme => ({
  textContainer: {
    "& p": {
      marginBottom: 20,
    },
  },
  title: {
    textAlign: "center",
    flex: 1,
    color: theme.palette.text.secondary,
  },
  appBar: {
    backgroundColor: "white",
    boxShadow: "none",
  },
  appBarBottom: {
    top: "auto",
    bottom: 0,
    backgroundColor: "white",
    boxShadow: "none",
  },
  epubViewerContainer: {
    position: "fixed",
    top: 52,
    right: 20,
    bottom: 52,
    left: 20,
  },
  prevButton: {
    position: "absolute",
    left: -10,
    top: "50%",
  },
  nextButton: {
    position: "absolute",
    right: -10,
    top: "50%",
  },
  pageIcon: {
    color: theme.palette.text.secondary,
    fontSize: "4rem",
  },
  slider: {
    margin: "0 20px",
  },
  sliderTrack: {
    color: theme.palette.text.secondary,
    ".Mui-disabled &": {
      color: theme.palette.grey[400],
    },
  },
  sliderRail: {
    color: theme.palette.grey[800],
    ".Mui-disabled &": {
      color: theme.palette.grey[400],
    },
  },
  sliderThumb: {
    backgroundColor: theme.palette.grey["A400"],
    "&:hover": {
      boxShadow: "0px 0px 0px 8px rgba(0, 0, 0, .1)",
    },
    "&:active": {
      boxShadow: "0px 0px 0px 14px rgba(0, 0, 0, .1)",
    },
    ".Mui-disabled &": {
      backgroundColor: theme.palette.grey[400],
    },
  },
}));

const EpubViewerDialog = () => {
  const classes = useStyles();

  const { media } = useSelector(state => state.mediaViewer);
  const dispatch = useDispatch();

  const onClose = () => dispatch({ type: HIDE_VIEWER });

  const [percentComplete, setPercentComplete] = useState(0);
  const [
    updateNextPercentOnLocationChange,
    setUpdateNextPercentOnLocationChange,
  ] = useState(true);
  const [locationsGenerated, setLocationsGenerated] = useState(false);
  const [atStart, setAtStart] = useState(false);
  const [atEnd, setAtEnd] = useState(false);
  const [tocDialogOpen, setTocDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [toc, setToc] = useState([]);
  const [searchResults, setSearchResults] = useState([]);

  const debouncedOpenBookToPercent = useCallback(
    debounce(percent => {
      const cfi = readerRef.current.book.locations.cfiFromPercentage(
        percent / 100,
      );
      readerRef.current.rendition.display(cfi);
    }, 200),
    [],
  );

  const handleLocationChange = () => {
    const { book, location } = readerRef.current.rendition;

    const percentComplete = Math.round(
      // book.locations.percentageFromCfi(location.end.cfi) * 100,
      book.locations.percentageFromCfi(location.start.cfi) * 100,
    );

    if (updateNextPercentOnLocationChange) {
      setPercentComplete(percentComplete);
    } else {
      setUpdateNextPercentOnLocationChange(true);
    }

    setAtStart(location.atStart);
    setAtEnd(location.atEnd);
  };

  const generateLocations = () => {
    const locations = readerRef.current.book.locations.generate(1000);
    locations.then(() => {
      setLocationsGenerated(true);
      handleLocationChange();
    });
  };

  const handleTocDialogClose = () => {
    setTocDialogOpen(false);
  };

  const handleTocItemClick = item => {
    readerRef.current.rendition.display(item.href);
    setTocDialogOpen(false);
  };

  const handleSearch = query => {
    handleClearSearch();

    const { book, rendition } = readerRef.current;

    const search = Promise.all(
      book.spine.spineItems.map(item =>
        item
          .load(book.load.bind(book))
          .then(item.find.bind(item, query))
          .finally(item.unload.bind(item)),
      ),
    ).then(results => Promise.resolve([].concat.apply([], results)));

    search.then(results => {
      setSearchResults(results);
      results.forEach(result => {
        rendition.annotations.highlight(result.cfi);
      });
    });
  };

  const handleClearSearch = () => {
    const { rendition } = readerRef.current;

    forEach(result => {
      rendition.annotations.remove(result.cfi, "highlight");
    }, searchResults);

    setSearchResults([]);
  };

  const handleSearchDialogClose = () => {
    setSearchDialogOpen(false);
  };

  const handleSearchItemClick = item => {
    readerRef.current.rendition.display(item.cfi);
    setSearchDialogOpen(false);
  };

  const url = media.epubFileUrl;
  const readerRef = useRef();

  // Load ePubs with or without tables of content
  const [location, setLocation] = useState(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const book = ePub(url);

    // Wait for navigation and spine to load
    Promise.all([book.loaded.navigation, book.ready])
      .then(([navigation]) => {
        const toc = navigation?.toc || [];
        if (toc.length > 0 && toc[0].href) {
          setLocation(toc[0].href);
        } else if (book.spine?.spineItems?.length > 0) {
          setLocation(book.spine.spineItems[0].href);
        } else {
          console.error("EPUB has no TOC or spine.");
        }
        setReady(true);
      })
      .catch(err => {
        console.error("Failed to load EPUB:", err);
      });
  }, [url]);

  return (
    <Dialog
      fullScreen={true}
      onClose={onClose}
      aria-label="epub viewer"
      open={true}
    >
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar variant="dense">
          <Typography variant="h5" className={classes.title}>
            {media.title}
          </Typography>
          <IconButton
            edge="end"
            // color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>
      <div className={classes.epubViewerContainer}>
        {ready && location && (
          <EpubView
            url={url}
            ref={readerRef}
            loadingView={
              <CircularProgress
                style={{
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  marginLeft: "-50px",
                  // marginTop: "-20px",
                }}
              />
            }
            location={location}
            locationChanged={handleLocationChange}
            getRendition={() => {
              generateLocations();
            }}
            tocChanged={toc => {
              setToc(toc);
            }}
          />
        )}

        {!atStart && (
          <IconButton
            className={classes.prevButton}
            aria-label="previous"
            onClick={() => {
              readerRef.current.prevPage();
            }}
          >
            <ChevronLeftIcon className={classes.pageIcon} />
          </IconButton>
        )}

        {!atEnd && (
          <IconButton
            className={classes.nextButton}
            aria-label="next"
            onClick={() => {
              readerRef.current.nextPage();
            }}
          >
            <ChevronRightIcon className={classes.pageIcon} />
          </IconButton>
        )}
      </div>
      <AppBar position="fixed" className={classes.appBarBottom}>
        <Toolbar variant="dense">
          <IconButton
            disabled={isEmpty(toc)}
            aria-label="table of contents"
            onClick={() => {
              setTocDialogOpen(true);
            }}
          >
            <TocIcon />
          </IconButton>
          <IconButton
            aria-label="search"
            onClick={() => {
              setSearchDialogOpen(true);
            }}
          >
            <SearchIcon />
          </IconButton>
          <Slider
            className={classes.slider}
            disabled={!locationsGenerated}
            value={percentComplete}
            onChange={(event, percent) => {
              setUpdateNextPercentOnLocationChange(false);
              setPercentComplete(percent);
              debouncedOpenBookToPercent(percent);
            }}
            classes={{
              rail: classes.sliderRail,
              track: classes.sliderTrack,
              thumb: classes.sliderThumb,
            }}
          />
        </Toolbar>
      </AppBar>
      <TocDialog
        toc={toc}
        open={tocDialogOpen}
        onClose={handleTocDialogClose}
        onItemClick={handleTocItemClick}
      />
      <SearchDialog
        open={searchDialogOpen}
        searchResults={searchResults}
        onSearch={handleSearch}
        onClear={handleClearSearch}
        onClose={handleSearchDialogClose}
        onItemClick={handleSearchItemClick}
      />
    </Dialog>
  );
};

export default EpubViewerDialog;
